/**
 * Desglose del costo de una entrada por compra en el detalle del movimiento.
 *
 * `StockMovement.price` cambió de significado en el monolito con la épica
 * costo-precio-movimientos (`tecopos-server`, commit `02309aeb5` del 2026-07-28,
 * `bulkEntryStockProduct`):
 *
 *   - ANTES guardaba el TOTAL de la entrada (unitario × cantidad).
 *   - DESDE ENTONCES guarda el COSTO UNITARIO en la moneda de la operación.
 *
 * Esta web se quedó congelada con la lectura vieja —dividía el importe entre la
 * cantidad para sacar el unitario—, así que desde que aquel cambio salió a producción
 * pintaba el importe tecleado por el usuario en la casilla del TOTAL (sin multiplicar
 * por la cantidad) y un unitario dividido de más. De ahí el «pongas lo que pongas, te
 * pone el total» que reportan los clientes.
 *
 * La frontera entre los dos contratos es la configuración pública
 * `movement_cost_valid_from` (una por región, la siembra la migración
 * `20260728160000` del monolito): un movimiento anterior a esa fecha SÍ tiene el total
 * guardado y hay que seguir leyéndolo como antes, o se rompería el histórico del
 * cliente. Sin fecha configurada se asume el contrato nuevo, que es el que rige hoy.
 */
export interface DesgloseCostoEntrada {
  /** Costo de una unidad. */
  unitario: number
  /** Costo de toda la entrada. */
  total: number
}

/**
 * @param importe        `price.amount` del movimiento, tal como llega del backend.
 * @param cantidad       `quantity` del movimiento.
 * @param esEstandarNuevo `true` si el movimiento es posterior a la fecha de vigencia.
 */
export const desglosarCostoEntrada = (
  importe: number | string | null | undefined,
  cantidad: number | string | null | undefined,
  esEstandarNuevo: boolean
): DesgloseCostoEntrada => {
  const importeLeido = Number(importe)
  const cantidadLeida = Math.abs(Number(cantidad))

  const importeValido = Number.isFinite(importeLeido) ? importeLeido : 0
  const cantidadValida = Number.isFinite(cantidadLeida) ? cantidadLeida : 0

  if (esEstandarNuevo) {
    return {
      unitario: importeValido,
      total: importeValido * cantidadValida,
    }
  }

  // Contrato viejo: el importe guardado ES el total de la entrada. La guarda de
  // cantidad cero evita el Infinity/NaN que la pantalla pintaba antes de este arreglo.
  return {
    unitario:
      cantidadValida > 0 ? importeValido / cantidadValida : importeValido,
    total: importeValido,
  }
}
